import React, { useContext, useEffect, useState } from 'react';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import axiosInstance from '../../../services/axiosInstance';
import { UserContext } from '../../../hooks';
import {
  isValidDni,
  isValidEmail,
  isValidPhone,
  MAX_DNI_PHONE_LENGTH,
  normalizeDni,
  normalizePhone,
} from '../../../utils/validation';

const AddPlayerDialog = ({
  open,
  onClose,
  members,
  players,
  booking,
  setBooking,
  setPlayers,
}) => {
  const { user, isMember } = useContext(UserContext);
  const [newPlayer, setNewPlayer] = useState({
    isMember: true,
    memberId: undefined,
    nonMemberName: '',
    nonMemberSurname: '',
    nonMemberEmail: '',
    nonMemberDni: '',
    nonMemberPhone: '',
    // isUca: false, // Nuevo campo para marcar si el no socio es uca
    usedInvitation: false, // Nuevo campo para marcar si el socio usa una invitación
  });
  const [invitationsUsed, setInvitationsUsed] = useState(null);
  const [error] = useState('');

  useEffect(() => {
    if (open) {
      axiosInstance
        .get('/users/invitations')
        .then(response => {
          setInvitationsUsed(response.invitationsUsed);
        })
        .catch(error => {
          console.error('Error al obtener invitaciones:', error);
        });
    }
  }, [open]);

  const handleAddPlayer = async () => {
    if (players.length >= 4) {
      toast.error('No se pueden añadir más de 4 jugadores.');
      return;
    }

    if (newPlayer.isMember && !newPlayer.memberId) {
      toast.error('Selecciona un socio válido.');
      return;
    }

    if (
      !newPlayer.isMember &&
      (!newPlayer.nonMemberName ||
        !newPlayer.nonMemberSurname ||
        !newPlayer.nonMemberEmail ||
        !newPlayer.nonMemberDni ||
        !newPlayer.nonMemberPhone)
    ) {
      toast.error(
        'Nombre, apellido, email, DNI y teléfono son obligatorios para jugadores no socios.'
      );
      return;
    }

    if (!newPlayer.isMember && !isValidEmail(newPlayer.nonMemberEmail)) {
      toast.error('Introduce un email válido para el jugador no socio.');
      return;
    }

    if (!newPlayer.isMember && !isValidDni(newPlayer.nonMemberDni)) {
      toast.error(
        'El DNI/NIE del jugador no socio es obligatorio y debe tener máximo 20 caracteres.'
      );
      return;
    }

    if (!newPlayer.isMember && !isValidPhone(newPlayer.nonMemberPhone)) {
      toast.error(
        'El teléfono del jugador no socio es obligatorio y debe tener máximo 20 caracteres.'
      );
      return;
    }

    if (
      newPlayer.isMember &&
      newPlayer.memberId === booking.userBooked._id &&
      !newPlayer.nonMemberName
    ) {
      toast.error('Por favor añade la relación familiar.');
      return;
    }

    let playerToAdd = newPlayer.isMember
      ? { ...newPlayer }
      : {
          ...newPlayer,
          nonMemberEmail: newPlayer.nonMemberEmail.trim().toLowerCase(),
          nonMemberSurname: newPlayer.nonMemberSurname.trim(),
          nonMemberDni: normalizeDni(newPlayer.nonMemberDni),
          nonMemberPhone: normalizePhone(newPlayer.nonMemberPhone),
        };

    // Si es miembro, agregar información adicional del socio seleccionado
    if (newPlayer.isMember) {
      const selectedMember = members.find(
        member => member._id === newPlayer.memberId
      );
      if (selectedMember) {
        playerToAdd = {
          ...playerToAdd,
          memberId: {
            _id: selectedMember._id, // ID del socio
            name: selectedMember.name, // Nombre
            surname: selectedMember.surname || '', // Apellido
            image: selectedMember.image || '', // Foto de perfil
            email: selectedMember.email, // Email
            role: selectedMember.role, // Role
          },
        };
      } else {
        if (!newPlayer.nonMemberName) {
          toast.error('Proporciona al menos el nombre de tu familiar.');
          return;
        }

        playerToAdd = {
          ...playerToAdd,
          memberId: booking.userBooked,
        };
      }
    }

    // Enviar la solicitud al backend
    try {
      const response = await axiosInstance.put(
        `/users/booking/${booking._id}`,
        {
          players: [...players, playerToAdd], // Agregar el nuevo jugador a la lista
        }
      );

      if (response.success) {
        setBooking(response.booking);
        setPlayers(response.booking.players); // Actualizar el estado local
        setNewPlayer({
          isMember: true,
          memberId: undefined,
          nonMemberName: '',
          nonMemberSurname: '',
          nonMemberEmail: '',
          nonMemberDni: '',
          nonMemberPhone: '',
          isUca: false,
          usedInvitation: false,
        }); // Reiniciar el formulario
        console.log(1);
        toast.success('Jugador añadido y guardado exitosamente.');
        onClose(); // Cerrar el diálogo
      } else {
        toast.error(response.msg || 'Error al guardar el jugador.');
      }
    } catch (error) {
      console.error('Error al guardar el jugador:', error);
      toast.error('No se pudo guardar el jugador. Inténtalo de nuevo.');
    }
  };

  const handleCheckboxChange = field => event => {
    setNewPlayer(prev => ({
      ...prev,
      [field]: event.target.checked,
      ...(field === 'isUca' && event.target.checked
        ? { usedInvitation: false }
        : field === 'usedInvitation' && event.target.checked
          ? { isUca: false }
          : {}),
    }));
  };

  // Filtrar los socios ya añadidos
  const availableMembers = members.filter(
    member =>
      !players.some(
        player => player.isMember && player.memberId?._id === member._id
      )
  );

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Añadir Nuevo Jugador</DialogTitle>
      <DialogContent>
        {/* Selector para definir si es socio, familiar o no */}
        <RadioGroup
          row
          value={
            newPlayer.memberId === booking.userBooked._id
              ? 'family'
              : newPlayer.isMember
                ? 'member'
                : 'no_member'
          }
          onChange={e =>
            setNewPlayer(prev => ({
              ...prev,
              isMember:
                e.target.value === 'member' || e.target.value === 'family',
              memberId:
                e.target.value === 'family'
                  ? booking.userBooked._id
                  : e.target.value === 'member'
                    ? prev.memberId
                    : undefined,
              nonMemberName:
                e.target.value === 'member' || e.target.value === 'family'
                  ? ''
                  : prev.nonMemberName,
              nonMemberSurname:
                e.target.value === 'member' || e.target.value === 'family'
                  ? ''
                  : prev.nonMemberSurname,
              nonMemberEmail:
                e.target.value === 'member' || e.target.value === 'family'
                  ? ''
                  : prev.nonMemberEmail,
              nonMemberDni:
                e.target.value === 'member' || e.target.value === 'family'
                  ? ''
                  : prev.nonMemberDni,
              nonMemberPhone:
                e.target.value === 'member' || e.target.value === 'family'
                  ? ''
                  : prev.nonMemberPhone,
              isUca: false,
              usedInvitation: false,
            }))
          }
        >
          <FormControlLabel value="member" control={<Radio />} label="Socio" />
          {['admin', 'user-adult', 'user-padel', 'vip'].includes(user.role) && (
            <FormControlLabel
              value="family"
              control={<Radio />}
              label="Familiar"
            />
          )}
          <FormControlLabel
            value="no_member"
            control={<Radio />}
            label="No Socio"
          />
        </RadioGroup>
        {/* Campo para socios */}
        {newPlayer.isMember && newPlayer.memberId !== booking.userBooked._id ? (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Seleccionar Socio</InputLabel>
            <Select
              value={newPlayer.memberId}
              onChange={e =>
                setNewPlayer(prev => ({
                  ...prev,
                  memberId: e.target.value,
                }))
              }
            >
              {availableMembers.map(member => (
                <MenuItem key={member._id} value={member._id}>
                  {`${member.name || ''} ${member.surname || ''}`.trim()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : newPlayer.isMember &&
          newPlayer.memberId === booking.userBooked._id ? (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Relación Familiar</InputLabel>
            <Select
              value={newPlayer.nonMemberName}
              onChange={e =>
                setNewPlayer(prev => ({
                  ...prev,
                  nonMemberName: e.target.value,
                }))
              }
            >
              <MenuItem value="Cónyuge">Cónyuge</MenuItem>
              <MenuItem value="Hijo">Hijo/a menor de 25 años</MenuItem>
            </Select>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              * Recuerda que esta opción es solo para familiares directos del
              núcleo familiar.
            </Typography>
          </FormControl>
        ) : (
          <>
            {/* Campos para jugadores no socios */}
            <TextField
              fullWidth
              label="Nombre del Jugador"
              value={newPlayer.nonMemberName}
              onChange={e =>
                setNewPlayer(prev => ({
                  ...prev,
                  nonMemberName: e.target.value,
                }))
              }
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Apellido del Jugador"
              value={newPlayer.nonMemberSurname}
              onChange={e =>
                setNewPlayer(prev => ({
                  ...prev,
                  nonMemberSurname: e.target.value,
                }))
              }
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email del Jugador"
              value={newPlayer.nonMemberEmail}
              onChange={e =>
                setNewPlayer(prev => ({
                  ...prev,
                  nonMemberEmail: e.target.value,
                }))
              }
            />
            <TextField
              fullWidth
              label="DNI del Jugador"
              value={newPlayer.nonMemberDni}
              inputProps={{ maxLength: MAX_DNI_PHONE_LENGTH }}
              onChange={e =>
                setNewPlayer(prev => ({
                  ...prev,
                  nonMemberDni: e.target.value.toUpperCase(),
                }))
              }
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              label="Teléfono del Jugador"
              type="tel"
              value={newPlayer.nonMemberPhone}
              inputProps={{ maxLength: MAX_DNI_PHONE_LENGTH }}
              onChange={e =>
                setNewPlayer(prev => ({
                  ...prev,
                  nonMemberPhone: e.target.value,
                }))
              }
              sx={{ mt: 2 }}
            />
            {/*<FormControlLabel*/}
            {/*  control={*/}
            {/*    <Checkbox*/}
            {/*      checked={newPlayer.isUca}*/}
            {/*      onChange={handleCheckboxChange('isUca')}*/}
            {/*    />*/}
            {/*  }*/}
            {/*  label="Es usuario de la UCA"*/}
            {/*/>*/}
            {booking.courtId.type === 'tenis' &&
              isMember &&
              players.length < 2 &&
              invitationsUsed < 2 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newPlayer.usedInvitation}
                      onChange={handleCheckboxChange('usedInvitation')}
                    />
                  }
                  label={`Usar invitación (descuento 5€) - Quedan ${2 - invitationsUsed}`}
                />
              )}
          </>
        )}
        {/* Mostrar error si no se cumple la validación */}
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancelar
        </Button>
        <Button onClick={handleAddPlayer} variant="contained" color="primary">
          Añadir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPlayerDialog;
